import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import CONFIG from '../config';
import type { CommonReponse, ResponseError, ServerReponse, SuccessReponse } from '../types/CommonReponse';

import type { Demande,  DemandeInsert, DemandeOperatorUpdate, DemandeParameters, DemandeUpdate } from '../types/Demande';

import type { Profil, User } from '../types/Users';
import type { Fond, FondChart } from '../types/Fond';
import type { Transactions } from '../types/Transaction';
import type { QueryParameters } from '../types/QueryParameters';
import { buildParams } from '../tools/utils';
import type { TypeFrais } from '../types/TypeFrais';
import type { Metas, MetasInsert } from '../types/Metas';
import type { Blocs, BlocsInsert } from '../types/Blocs';
import type { Reglements, ReglementsInsert } from '../types/Reglements';
import type { Traductions, TraductionsInsert } from '../types/Traductions';
import type { DonsInsert, Dons, DonPayements } from '../types/Dons';
import type { LogMessages } from '../types/LogMessages';
import type { PagesInsert, Pages } from '../types/Pages';
import type { Messages } from '../types/Messages';


function providesList<T extends string>(
  resultsWithIds: { id: string | number }[] | undefined,
  tagType: T,
) {
  return resultsWithIds
    ? [
        { type: tagType, id: 'LIST' },
        ...resultsWithIds.map(({ id }) => ({ type: tagType, id })),
      ]
    : [{ type: tagType, id: 'LIST' }]
}





export const userApi = createApi({
    reducerPath: 'userApi',
    baseQuery: fetchBaseQuery({
        baseUrl: CONFIG.SERVER,
        credentials: 'include', // include cookies
        
        prepareHeaders: (headers) => {        
            headers.set('Content-Type','application/json');
            return headers;
        },

    }),
  

    tagTypes: [
        'Demandes',
        'Profil',
        'Fonds',
        'Users',
        'TypeFrais',
        'Transactions',
        'Metas',
        'Blocs',
        'Reglements',
        'Traductions',
        'Dons',
        'DonPayements',
        'Donations',
        'Logs',
        'FondChart',
        'Pages',
        'Messages'
    ],
    endpoints: build => ({
        lookupDemandes: build.query<CommonReponse<Demande>,DemandeParameters>({
            query: (params:DemandeParameters) => {
                const p=buildParams(params);
                return `/demandes?${p}`
            },
            providesTags: (result) => providesList(result?.data??[],'Demandes'),
            // Refetch when the params arg changes
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
        }),
        demandeById: build.query<Demande,string>({
            query: (demandeId) => `/demandes/${demandeId}`,
            providesTags: (result) => result ? [{type:'Demandes',id:result.id}]:[],

        }),
        addDemande:build.mutation<Demande,DemandeInsert>({
            query: (demande:DemandeInsert) => ({
                url: '/demandes',
                method: 'POST',
                body: JSON.stringify(demande),
            }),
            transformResponse: (response:SuccessReponse<Demande>) =>{return response.data},
            transformErrorResponse:(response:ResponseError) => {return response},
            invalidatesTags: () => {
                return [{type:'Demandes',id:'LIST'}];
            },
        }),
        updateDemande:build.mutation<Demande,DemandeUpdate | DemandeOperatorUpdate>({
            query: (demande:DemandeUpdate|DemandeOperatorUpdate) => ({
                url: '/demandes/'+demande.id.toString(),
                method: 'PUT',
                body: JSON.stringify(demande),
            }),
            invalidatesTags: (_result, _error, param) => {
                return [{type:'Demandes',id:param.id},{type:'Fonds',id:param.fond_id}];
            },
            transformErrorResponse:(response:ResponseError) => {return response},
            transformResponse:(response:SuccessReponse<Demande>) => {return response.data},
        }),
        // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
        myProfil: build.query<Profil|undefined,void>({
            query: () => '/profil',
            providesTags: (response) => response ? [{ type: 'Profil', id:response.id }]:[],
        }),
        editProfil: build.mutation<Profil,Profil & Pick<Profil,'id'>>({
            query: (profil:Profil) => ({
                url: '/profil',
                method: 'PUT',
                body: JSON.stringify(profil),
            }),
            invalidatesTags: (_result, _error, param) => {
                return [{type:'Profil',id:param.id}];
            },
            transformErrorResponse:(response:ResponseError) => {return response},
            transformResponse:(response:SuccessReponse<Profil>) => {return response.data},
        }),

        lookupFonds: build.query<CommonReponse<Fond>,QueryParameters>({
            query: (params:QueryParameters) => {
                const p=buildParams(params);
                return `/fonds?${p}`
            },
            transformResponse: (response:  CommonReponse<Fond>) =>{return response},
            providesTags: (result) => providesList(result?.data??[],'Fonds'),
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
        }),
        lookupFondCharts: build.query<ServerReponse<FondChart[]>,QueryParameters>({
            query: (params:QueryParameters) => {
                const p=buildParams(params);
                return `/fonds/charts?${p}`
            },
            transformResponse: (response:  ServerReponse<FondChart[]>) =>{return response},
            providesTags: (result) => providesList(result?.data??[],'FondChart'),
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
        }),
        fondById: build.query<Fond, string>({
            query: (fondId:string) => `/fonds/${fondId}`,
            providesTags: (result) => result ? [{type:'Fonds',id:result.id}]:[],
        }),
        // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
        fondPerso: build.query<Fond, void>({
            query: () => `/fonds/perso`,
            providesTags: (result) => result ? [{type:'Fonds',id:result.id}]:[],
        }),

        addFond:build.mutation<Fond,Partial<Fond>>({
            query: (fond:Partial<Fond>) => ({
                url: '/fonds',
                method: 'POST',
                body: JSON.stringify(fond),
            }),
            invalidatesTags: () => {
                return [{type:'Fonds',id:'LIST'}];
            },
            transformErrorResponse:(response:ResponseError) => {return response},
            transformResponse:(response:SuccessReponse<Fond>) => {return response.data},
        }),
        updateFond:build.mutation<Fond,Partial<Fond> & Pick<Fond,'id'>>({
            query: (fond:Partial<Fond> & Pick<Fond,'id'>) => ({
                url: '/fonds/'+fond.id.toString(),
                method: 'PUT',
                body: JSON.stringify(fond),
            }),
            invalidatesTags: (_result, _error, param) => {
                return [{type:'Fonds',id:param.id}];
            },
            transformErrorResponse:(response:ResponseError) => {return response},
            transformResponse:(response:SuccessReponse<Fond>) => {return response.data},
        }),

        lookupUsers: build.query<CommonReponse<User>,QueryParameters>({
            query: (params:QueryParameters) => {
                const p=buildParams(params);
                return `/inscriptions?${p}`
            },
            transformResponse: (response:  CommonReponse<User>) =>{return response},
            providesTags: (result) => providesList(result?.data??[],'Users'),
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },

        }),
        userById: build.query<User,string>({
            query: (id:string) => {
                return `/inscription/${id}`
            },
            providesTags: (result) => result ? [{ type: 'Users',id: result.id }] : [],
        }),
        updateUser:build.mutation<User,Partial<User> & Pick<User,'id'>>({
            query: (user:Partial<User> & Pick<User,'id'>) => ({
                url: '/inscription/'+user.id.toString(),
                method: 'PUT',
                body: JSON.stringify(user),
            }),
            transformErrorResponse:(response:ResponseError) => {return response},
            transformResponse:(response:SuccessReponse<User>) => {return response.data},
            invalidatesTags: (_result, _error, param) => {
                return [{type:'Users',id:param.id}];
            },

        }),
        addUser:build.mutation<User,Partial<User>&{password?:string,password_confirm?:string}>({
            query: (user:Partial<User>) => ({
                url: '/inscriptions',
                method: 'POST',
                body: JSON.stringify(user),
            }),
            transformErrorResponse:(response:ResponseError) => {return response},
            transformResponse:(response:SuccessReponse<User>) => {return response.data},
            invalidatesTags: () => {
                return [{type:'Users',id:'LIST'}];
            },
        }),

        lookupTransactions:build.query<CommonReponse<Transactions>,QueryParameters>({
            query: (params:QueryParameters) => {
                const p=buildParams(params);
                return `/transactions?${p}`
            },
            transformResponse: (response:  CommonReponse<Transactions>) =>{return response},
            providesTags: (result) => providesList(result?.data??[],'Transactions'),
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
        }),
        transactionsById:build.query<Transactions,string>({
            query: (id:string) => {
                return `/transactions/${id}`
            },
            providesTags: (result) => result ? [{ type: 'Transactions',id: result.id }] : [],
        }),
        addTransaction:build.mutation<Transactions,Partial<Transactions>>({
            query: (transaction:Partial<Transactions>) => ({
                url: '/transactions',
                method: 'POST',
                body: JSON.stringify(transaction),
            }),
            invalidatesTags: (_result, _error, param) => {

                return [{type:'Fonds',id:param.fond_id},{ type: 'Transactions',id:'LIST'}];
            },
            transformErrorResponse:(response:ResponseError) => {return response},
            transformResponse:(response:SuccessReponse<Transactions>) => {return response.data},
        }),
        updateTransactions:build.mutation<Transactions,Partial<Transactions> & Pick<Transactions,'id'>>({
            query: (transaction:Partial<Transactions> & Pick<Transactions,'id'>) => ({
                url: '/transactions/'+transaction.id.toString(),
                method: 'PUT',
                body: JSON.stringify(transaction),
            }),
            transformErrorResponse:(response:ResponseError) => {return response},
            transformResponse:(response:SuccessReponse<Transactions>) => {return response.data},
            invalidatesTags: (_result, _error, param) => {
                return [{type:'Transactions',id:param.id}];
            },

        }),

        lookupTypeFrais:build.query<CommonReponse<TypeFrais>,QueryParameters>({
            query: (params:QueryParameters) => {
                const p=buildParams(params);
                return `/type_frais?${p}`
            },
            providesTags: (result) => providesList(result?.data??[],'TypeFrais'),
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
        }),
        typeFraisById: build.query<TypeFrais,string>({
            query: (id:string) => {
                return `/type_frais/${id}`
            },
            providesTags: (result) => result ? [{ type: 'TypeFrais',id: result.id }] : [],
        }),
        addTypeFrais:build.mutation<TypeFrais,Partial<TypeFrais>>({
            query: (typefrais:Partial<TypeFrais>) => ({
                url: '/type_frais',
                method: 'POST',
                body: JSON.stringify(typefrais),
            }),
            transformResponse: (response:SuccessReponse<TypeFrais>) =>{return response.data},
            transformErrorResponse:(response:ResponseError) => {return response},
            invalidatesTags: () => {
                return [{type:'TypeFrais',id:'LIST'}];
            },
        }),
        updateTypeFrais:build.mutation<TypeFrais,Partial<TypeFrais> & Pick<TypeFrais,'id'>>({
            query: (typefrais:Partial<TypeFrais> & Pick<TypeFrais,'id'>) => ({
                url: '/type_frais/'+typefrais.id.toString(),
                method: 'PUT',
                body: JSON.stringify(typefrais),
            }),
            transformErrorResponse:(response:ResponseError) => {return response},
            transformResponse:(response:SuccessReponse<TypeFrais>) => {return response.data},
            invalidatesTags: (_result, _error, param) => {
                return [{type:'TypeFrais',id:param.id}];
            },
        }),

        lookupMetas: build.query<CommonReponse<Metas>,QueryParameters>({
            query: (params:QueryParameters) => {
                const p=buildParams(params);
                return `/metas?${p}`
            },
            transformResponse: (response:  CommonReponse<Metas>) =>{return response},
            providesTags: (result) => providesList(result?.data??[],'Metas'),
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
        }),
        addMetas: build.mutation<Metas,MetasInsert>({
            query: (meta:MetasInsert) => ({
                url: '/metas',
                method: 'POST',
                body: JSON.stringify(meta),
            }),
            transformResponse: (response:SuccessReponse<Metas>) =>{return response.data},
            transformErrorResponse:(response:ResponseError) => {return response},
            invalidatesTags: () => {
                return [{type:'Metas',id:'LIST'}];
            },
        }),
        updateMetas: build.mutation<Metas,Partial<Metas> & Pick<Metas, 'id'>>({
            query: (meta:Metas) => ({
                url: '/metas/'+meta.id.toString(),
                method: 'PUT',
                body: JSON.stringify(meta),
            }),
            transformResponse: (response:SuccessReponse<Metas>) =>{return response.data},
            transformErrorResponse:(response:ResponseError) => {return response},
            invalidatesTags: (_result, _error, param) => {
                return [{type:'Metas',id:param.id}];
            },
        }),
        metasById: build.query<Metas,string>({
            query: (id:string) => {
                return `/metas/${id}`
            },
            providesTags: (result) => result ? [{ type: 'Metas',id: result.id }] : [],
        }),

        lookupBlocs: build.query<CommonReponse<Blocs>,QueryParameters>({
            query: (params:QueryParameters) => {
                const p=buildParams(params);
                return `/blocs?${p}`
            },
            transformResponse: (response:  CommonReponse<Blocs>) =>{return response},
            providesTags: (result) => providesList(result?.data??[],'Blocs'),
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
        }),
        addBlocs: build.mutation<Blocs,BlocsInsert>({
            query: (bloc:BlocsInsert) => ({
                url: '/blocs',
                method: 'POST',
                body: JSON.stringify(bloc),
            }),
            transformResponse: (response:SuccessReponse<Blocs>) =>{return response.data},
            transformErrorResponse:(response:ResponseError) => {return response},
            invalidatesTags: () => {
                return [{type:'Blocs',id:'LIST'}];
            },
        }),
        updateBlocs: build.mutation<Blocs,Partial<Blocs> & Pick<Blocs, 'id'>>({
            query: (bloc:Blocs) => ({
                url: '/blocs/'+bloc.id.toString(),
                method: 'PUT',
                body: JSON.stringify(bloc),
            }),
            transformResponse: (response:SuccessReponse<Blocs>) =>{return response.data},
            transformErrorResponse:(response:ResponseError) => {return response},
            invalidatesTags: (_result, _error, param) => {
                return [{type:'Blocs',id:param.id}];
            },
        }),
        blocsById: build.query<Blocs,string>({
            query: (id:string) => {
                return `/blocs/${id}`
            },
            providesTags: (result) => result ? [{ type: 'Blocs',id: result.id }] : [],
        }),




        lookupTraductions: build.query<CommonReponse<Traductions>,QueryParameters>({
            query: (params:QueryParameters) => {
                const p=buildParams(params);
                return `/traductions?${p}`
            },
            transformResponse: (response:  CommonReponse<Traductions>) =>{return response},
            providesTags: (result) => providesList(result?.data??[],'Traductions'),
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
        }),
        addTraductions: build.mutation<Traductions,TraductionsInsert>({
            query: (traduction:TraductionsInsert) => ({
                url: '/traductions',
                method: 'POST',
                body: JSON.stringify(traduction),
            }),
            transformResponse: (response:SuccessReponse<Traductions>) =>{return response.data},
            transformErrorResponse:(response:ResponseError) => {return response},
            invalidatesTags: () => {
                return [{type:'Traductions',id:'LIST'}];
            },
        }),
        updateTraductions: build.mutation<Traductions,Partial<Traductions> & Pick<Traductions, 'id'>>({
            query: (traduction:Traductions) => ({
                url: '/traductions/'+traduction.id.toString(),
                method: 'PUT',
                body: JSON.stringify(traduction),
            }),
            transformResponse: (response:SuccessReponse<Traductions>) =>{return response.data},
            transformErrorResponse:(response:ResponseError) => {return response},
            invalidatesTags: (_result, _error, param) => {
                return [{type:'Traductions',id:param.id}];
            },
        }),
        traductionsById: build.query<Traductions,string>({
            query: (id:string) => {
                return `/traductions/${id}`
            },
            providesTags: (result) => result ? [{ type: 'Traductions',id: result.id }] : [],
        }),





        lookupReglements: build.query<CommonReponse<Reglements>,QueryParameters>({
            query: (params:QueryParameters) => {
                const p=buildParams(params);
                return `/reglements?${p}`
            },
            transformResponse: (response:  CommonReponse<Reglements>) =>{return response},
            providesTags: (result) => providesList(result?.data??[],'Reglements'),
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
        }),
        addReglements: build.mutation<Reglements,ReglementsInsert>({
            query: (reglement:ReglementsInsert) => ({
                url: '/reglements',
                method: 'POST',
                body: JSON.stringify(reglement),
            }),
            transformResponse: (response:SuccessReponse<Reglements>) =>{return response.data},
            transformErrorResponse:(response:ResponseError) => {return response},
            invalidatesTags: () => {
                return [{type:'Reglements',id:'LIST'}];
            },
        }),
        updateReglements: build.mutation<Reglements,Partial<Reglements> & Pick<Reglements, 'id'>>({
            query: (reglement:Reglements) => ({
                url: '/reglements/'+reglement.id.toString(),
                method: 'PUT',
                body: JSON.stringify(reglement),
            }),
            transformResponse: (response:SuccessReponse<Reglements>) =>{return response.data},
            transformErrorResponse:(response:ResponseError) => {return response},
            invalidatesTags: (_result, _error, param) => {
                return [{type:'Reglements',id:param.id}];
            },
        }),
        reglementsById: build.query<Reglements,string>({
            query: (id:string) => {
                return `/reglements/${id}`
            },
            providesTags: (result) => result ? [{ type: 'Reglements',id: result.id }] : [],
        }),
        // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
        currentReglement: build.query<Reglements, void>({
            query: () => {
                return `/reglements/last`
            },
            providesTags: (result) => result ? [{ type: 'Reglements',id: result.id }] : [],
        }),

        lookupDons: build.query<CommonReponse<Dons>,QueryParameters>({
            query: (params:QueryParameters) => {
                const p=buildParams(params);
                return `/dons?${p}`
            },
            transformResponse: (response:  CommonReponse<Dons>) =>{return response},
            providesTags: (result) => providesList(result?.data??[],'Dons'),
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
        }),

        addDons:build.mutation<Dons,Partial<DonsInsert>>({
            query: (don:Partial<DonsInsert> ) => ({
                url: '/dons',
                method: 'POST',
                body: JSON.stringify(don),
            }),
            transformResponse: (response:SuccessReponse<Dons>) =>{return response.data},
            transformErrorResponse:(response:ResponseError) => {return response},
            invalidatesTags: () => {
                return [{type:'Dons',id:'LIST'}];
            },
        }),

        donsById: build.query<Dons,string>({
            query: (id:string) => {
                return `/dons/${id}`
            },
            providesTags: (result) => result ? [{ type: 'Dons',id: result.id }] : [],
        }),

        lookupDonations: build.query<CommonReponse<Fond>,QueryParameters>({
            query: (params:QueryParameters) => {
                const p=buildParams(params);
                return `/donations?${p}`
            },
            transformResponse: (response:  CommonReponse<Fond>) =>{return response},
            providesTags: (result) => providesList(result?.data??[],'Donations'),
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
        }),
        donationsById: build.query<Fond,string>({
            query: (id:string) => {
                return `/donations/${id}`
            },
            providesTags: (result) => result ? [{ type: 'Donations',id: result.id }] : [],
        }),

        //LogMessages

        lookupLogs: build.query<CommonReponse<LogMessages>,QueryParameters>({
            query: (params:QueryParameters) => {
                const p=buildParams(params);
                return `/log_messages?${p}`
            },
            transformResponse: (response:  CommonReponse<LogMessages>) =>{return response},
            providesTags: (result) => providesList(result?.data??[],'Logs'),
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
        }),
        LogsById: build.query<LogMessages,string>({
            query: (id:string) => {
                return `/log_messages/${id}`
            },
            providesTags: (result) => result ? [{ type: 'Logs',id: result.id }] : [],
        }),


        //Pages

        lookupPages: build.query<CommonReponse<Pages>,QueryParameters>({
            query: (params:QueryParameters) => {
                const p=buildParams(params);
                return `/pages?${p}`
            },
            transformResponse: (response:  CommonReponse<Pages>) =>{return response},
            providesTags: (result) => providesList(result?.data??[],'Pages'),
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
        }),
        addPages: build.mutation<Pages,PagesInsert>({
            query: (page:PagesInsert) => ({
                url: '/pages',
                method: 'POST',
                body: JSON.stringify(page),
            }),
            transformResponse: (response:SuccessReponse<Pages>) =>{return response.data},
            transformErrorResponse:(response:ResponseError) => {return response},
            invalidatesTags: () => {
                return [{type:'Pages',id:'LIST'}];
            },
        }),
        updatePages: build.mutation<Pages,Partial<Pages> & Pick<Pages, 'id'>>({
            query: (page:Pages) => ({
                url: '/pages/'+page.id.toString(),
                method: 'PUT',
                body: JSON.stringify(page),
            }),
            transformResponse: (response:SuccessReponse<Pages>) =>{return response.data},
            transformErrorResponse:(response:ResponseError) => {return response},
            invalidatesTags: (_result, _error, param) => {
                return [{type:'Pages',id:param.id}];
            },
        }),
        pagesById: build.query<Pages,string>({
            query: (id:string) => {
                return `/pages/${id}`
            },
            providesTags: (result) => result ? [{ type: 'Pages',id: result.id }] : [],
        }),


        //Messages
        lookupMessages: build.query<CommonReponse<Messages>,QueryParameters>({
            query: (params:QueryParameters) => {
                const p=buildParams(params);
                return `/messages?${p}`
            },
            transformResponse: (response:  CommonReponse<Messages>) =>{return response},
            providesTags: (result) => providesList(result?.data??[],'Messages'),
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
        }),
        addMessages: build.mutation<Messages,Partial<Messages>>({
            query: (page:Partial<Messages>) => ({
                url: '/messages',
                method: 'POST',
                body: JSON.stringify(page),
            }),
            transformResponse: (response:SuccessReponse<Messages>) =>{return response.data},
            transformErrorResponse:(response:ResponseError) => {return response},
            invalidatesTags: () => {
                return [{type:'Messages',id:'LIST'}];
            },
        }),
        updateMessages: build.mutation<Messages,Partial<Messages> & Pick<Messages, 'id'>>({
            query: (message:Messages) => ({
                url: '/messages/'+message.id.toString(),
                method: 'PUT',
                body: JSON.stringify(message),
            }),
            transformResponse: (response:SuccessReponse<Messages>) =>{return response.data},
            transformErrorResponse:(response:ResponseError) => {return response},
            invalidatesTags: (_result, _error, param) => {
                return [{type:'Pages',id:param.id}];
            },
        }),
        messagesById: build.query<Pages,string>({
            query: (id:string) => {
                return `/messages/${id}`
            },
            providesTags: (result) => result ? [{ type: 'Messages',id: result.id }] : [],
        }),



        lookupDonPayements: build.query<CommonReponse<DonPayements>,QueryParameters>({
            query: (params:QueryParameters) => {
                const p=buildParams(params);
                return `/don_payements?${p}`
            },
            transformResponse: (response:  CommonReponse<DonPayements>) =>{return response},
            providesTags: (result) => providesList(result?.data??[],'Messages'),
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
        }),


    })
});

export const { 
    useLookupDemandesQuery, 
    useDemandeByIdQuery,
    useAddDemandeMutation,
    useUpdateDemandeMutation,

    useMyProfilQuery,
    useEditProfilMutation,

    useLookupFondsQuery,
    useLookupFondChartsQuery,
    useFondByIdQuery,
    useAddFondMutation,
    useUpdateFondMutation,
    useFondPersoQuery,


    useLookupUsersQuery,
    useUserByIdQuery,
    useUpdateUserMutation,
    useAddUserMutation,

    useAddTransactionMutation,
    useLookupTransactionsQuery,
    useTransactionsByIdQuery,
    useUpdateTransactionsMutation,

    useLookupTypeFraisQuery,
    useTypeFraisByIdQuery,
    useAddTypeFraisMutation,
    useUpdateTypeFraisMutation,

    useLookupMetasQuery,
    useAddMetasMutation,
    useUpdateMetasMutation,
    useMetasByIdQuery,

    useLookupBlocsQuery,
    useAddBlocsMutation,
    useUpdateBlocsMutation,
    useBlocsByIdQuery,

    useLookupTraductionsQuery,
    useAddTraductionsMutation,
    useUpdateTraductionsMutation,
    useTraductionsByIdQuery,


    useLookupReglementsQuery,
    useAddReglementsMutation,
    useUpdateReglementsMutation,
    useReglementsByIdQuery,
    useCurrentReglementQuery,
    useLookupDonsQuery,
    useAddDonsMutation,
    useDonsByIdQuery,

    useDonationsByIdQuery,
    useLookupDonationsQuery,

    useLogsByIdQuery,
    useLookupLogsQuery,


    useLookupPagesQuery,
    useAddPagesMutation,
    useUpdatePagesMutation,
    usePagesByIdQuery,

    useLookupMessagesQuery,
    useAddMessagesMutation,
    useUpdateMessagesMutation,
    useMessagesByIdQuery,

    useLookupDonPayementsQuery,
    

} = userApi